import React, { useState, useRef, useEffect } from 'react';

import {
  SendOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  AuditOutlined
} from '@ant-design/icons';
import { Input, Button, Space, Typography, Spin, message, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from '@components/shared/ai/components';

const { TextArea } = Input;
const { Text } = Typography;

/**
 * AIAssistantPanel - inline GoBot 對話面板
 *
 * 單一對話、無 sidebar。訊息只活在 session 中。
 * 訊息樣式統一用 shared/ai/components/ChatMessage：User 黑色 pill、Assistant 純文字。
 */
export default function AIAssistantPanel({
  paperSize,
  processing = false,
  onSendMessage,
  onClose
}) {
  const { t } = useTranslation();

  const [messages, setMessages] = useState(() => ([{
    role: 'assistant',
    content: t('shared.order_templates.ai_assistant.welcome_message')
  }]));
  const [inputValue, setInputValue] = useState('');

  const chatContainerRef = useRef(null);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) {
      message.warning(t('shared.order_templates.ai_assistant.empty_input'));
      return;
    }

    const userMessage = { role: 'user', content: trimmedInput };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');

    if (!onSendMessage) return;

    try {
      const aiResponse = await onSendMessage(trimmedInput, messages);
      if (aiResponse) {
        setMessages((prev) => [...prev, { role: 'assistant', content: aiResponse }]);
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const userMessageCount = messages.filter((m) => m.role === 'user').length;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 200px)',
        minHeight: '520px',
        border: '1px solid #e8e8e8',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: '#fff'
      }}
    >
      <div
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid #e8e8e8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 48
        }}
      >
        <Space size={2}>
          {paperSize?.width && paperSize?.height && (
            <Tooltip
              title={`${t('shared.order_templates.ai_assistant.info.paper_size')}: ${paperSize.width}mm × ${paperSize.height}mm`}
            >
              <InfoCircleOutlined style={{ color: '#1677ff' }} />
            </Tooltip>
          )}
        </Space>

        {/* Center: messages count */}
        <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
          {userMessageCount > 0
            ? t('operator.ai_chat.messages_count', { count: userMessageCount, defaultValue: '{{count}} messages' })
            : ''}
        </Text>

        <Space size={2}>
          {onClose && (
            <Tooltip title={t('common.button.close')}>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
              />
            </Tooltip>
          )}
        </Space>
      </div>

      {/* Messages */}
      <div
        ref={chatContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 24px',
          backgroundColor: '#fff'
        }}
      >
        {messages.map((msg, idx) => (
          <ChatMessage key={idx} message={msg} />
        ))}

        {processing && (
          <div style={{ textAlign: 'left', padding: '4px 4px 16px' }}>
            <Space>
              <Spin size="small" />
              <Text type="secondary">
                {t('shared.order_templates.ai_assistant.thinking')}
              </Text>
            </Space>
          </div>
        )}
      </div>

      {/* Input area */}
      <div
        style={{
          padding: '12px 16px 16px',
          backgroundColor: '#fff'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            border: '1px solid #e8e8e8',
            borderRadius: 9999,
            padding: '4px 6px 4px 18px',
            backgroundColor: '#fff'
          }}
        >
          <TextArea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('shared.order_templates.ai_assistant.input_placeholder')}
            autoSize={{ minRows: 1, maxRows: 5 }}
            disabled={processing}
            variant="borderless"
            style={{ flex: 1, resize: 'none', padding: '6px 0' }}
          />
          <Button
            shape="circle"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={processing}
            disabled={!inputValue.trim()}
            style={{
              background: inputValue.trim() ? '#595959' : '#d9d9d9',
              borderColor: inputValue.trim() ? '#595959' : '#d9d9d9',
              color: '#fff'
            }}
          />
        </div>
        <Text
          type="secondary"
          style={{ fontSize: 11, marginTop: 6, display: 'block' }}
        >
          {t('shared.order_templates.ai_assistant.hint')}
        </Text>
      </div>
    </div>
  );
}
